import type { ReactNode } from 'react';
import { X, GripVertical } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Common chrome for a dashboard tile: title row, drag handle (edit
 * mode only), close button (edit mode only), and a body slot. Tiles
 * provide their own body content; the frame owns the container,
 * rounding, borders, and edit-mode affordances.
 *
 * The drag handle has the class `tile-drag-handle` — react-grid-layout
 * is configured with `draggableHandle=".tile-drag-handle"` so dragging
 * only starts on the handle, not anywhere on the tile. This stops the
 * grid from intercepting clicks inside the tile body (e.g. clicks on
 * a chart's bar, hovering over a stat).
 */
export function TileFrame({
  title,
  isEditing,
  onRemove,
  children,
  className,
}: {
  title: string;
  isEditing: boolean;
  onRemove?: () => void;
  children: ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <Card
      className={cn(
        'h-full w-full gap-0 overflow-hidden py-0',
        isEditing && 'ring-1 ring-primary/40',
        className
      )}
    >
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          {isEditing && (
            <span
              className="tile-drag-handle cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
              aria-label="Drag tile"
              role="button"
            >
              <GripVertical className="h-4 w-4" />
            </span>
          )}
          <span className="truncate text-sm font-medium">{title}</span>
        </div>
        {isEditing && onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onRemove}
            aria-label="Remove tile"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>
    </Card>
  );
}
